"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { ButtonStyleType, sectionVariants, SpacingType } from "@/types";
import Image from '@/components/ui/Image';
import { useVideo, VideoIframe } from "../components/VideoIframe";
import parse from 'html-react-parser';
import { SectionWrapper } from "../components/SectionWraper";
import Link from "next/link";
import { cn, sectionBackgroundColors } from "@/lib/utils";
import { motion, AnimatePresence, useInView } from "@/components/LazyMotion";

interface Testimonial1Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  testimonials?: {
    badge?: {
      badge?: string;
    }[];
    content?: string;
    mediaType?: 'image' | 'video';
    image?: string;
    imageAltText?: string;
    video?: string;
    author?: string;
    company?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
  }[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const fadeUpVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (custom: number) => ({ 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      delay: custom * 0.2
    }
  })
};

const Testimonial1 = ({
  darkMode = false,
  spacing = 'xlarge',
  variant = 'A',
  usedInPageNav = false,
  pageNavTitle = "Testimonial 1",
  themeChangeOnScroll = false,
  testimonials,
  attributes,
}: Testimonial1Props) => {
  const { isVideoOpen, currentVideo, getVideoThumbnail, handleVideoClick, handleCloseVideo } = useVideo();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState<Set<string>>(new Set());

  // Preload images to prevent jumping
  useEffect(() => {
    if (testimonials) {
      testimonials.forEach((testimonial) => {
        if (testimonial.image) {
          const img = new window.Image();
          img.onload = () => {
            setImagesLoaded(prev => new Set(prev).add(testimonial.image!));
          };
          img.src = testimonial.image;
        }
      });
    }
  }, [testimonials]);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 40 : -40,
      opacity: 0.2
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 40 : -40,
      opacity: 0.2
    })
  };

  const swipeTransition = {
    duration: 0.32
  };
 
  const paginate = useCallback((newDirection: number) => {
    if (!testimonials) return;
    
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + newDirection;
      if (nextIndex < 0) return testimonials.length - 1;
      if (nextIndex >= testimonials.length) return 0;
      return nextIndex;
    });
  }, [testimonials]);

  const badgeRef = useRef(null);
  const headingRef = useRef(null);
  const contentRef = useRef(null);
  
  const isBadgeInView = useInView(badgeRef, { once: true, margin: "-100px" });
  const isHeadingInView = useInView(headingRef, { once: true, margin: "-100px" });
  // const isContentInView = useInView(contentRef, { once: true, margin: "20px" });

  // Reset initial load state when changing slides
  useEffect(() => {
    if (currentIndex !== 0) {
      setIsInitialLoad(false);
    }
  }, [currentIndex]);

  const getAnimationProps = (isInView: boolean, elementIndex: number) => {
    if (isInitialLoad && currentIndex === 0) {
      return {
        initial: "hidden",
        animate: isInView ? "visible" : "hidden",
        custom: elementIndex
      };
    }
    return {
      initial: "visible",
      animate: "visible",
      custom: elementIndex
    };
  };

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="testimonial1"
      attributes={attributes}
      className="relative"
    >
      <div className={cn(
        "relative z-10 overflow-hidden transition-all duration-300",
        isInitialLoad && testimonials?.[0]?.image && !imagesLoaded.has(testimonials[0].image) && "opacity-0"
      )}>
        <AnimatePresence initial={false} custom={direction} mode="wait"  data-aos="fade-up">
          {testimonials && testimonials.length > 0 && (
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={swipeTransition}
              drag={false}
              className="w-full"
            >
              <div className="container flex flex-col justify-between h-full flex-1 z-1 min-h-[700px] gap-36">
                <div className="flex flex-col gap-6 lg:gap-10 max-w-[1180px] flex-1">
                  {testimonials[currentIndex].badge && testimonials[currentIndex].badge.length > 0 && (
                    <motion.div 
                      ref={badgeRef}
                      variants={fadeUpVariants}
                      {...getAnimationProps(isBadgeInView, 0)}
                      className="flex flex-wrap gap-2"
                    >
                      {testimonials[currentIndex].badge.map((badge, index) => (
                        <Badge key={index} variant="outline" bold>
                          {badge.badge}
                        </Badge>
                      ))}
                    </motion.div>
                  )}
                  {testimonials[currentIndex].content && (
                    <motion.h3 
                      ref={headingRef}
                      variants={fadeUpVariants}
                      {...getAnimationProps(isHeadingInView, 1)}
                      className="flex-1"
                    >
                      {parse(testimonials[currentIndex].content)}
                    </motion.h3>
                  )}
                </div>

                <motion.div 
                  ref={contentRef}
                  variants={fadeUpVariants}
                  {...getAnimationProps(isHeadingInView, 1)}
                  className="flex flex-col md:flex-row gap-6 lg:gap-14 max-w-[1180px]"
                >
                  {testimonials[currentIndex].mediaType === 'image' && testimonials[currentIndex].image && (
                    <div className="relative md:w-[248px] aspect-[248/140] rounded-xl overflow-hidden">
                      <Image 
                        src={testimonials[currentIndex].image} 
                        alt={testimonials[currentIndex].imageAltText || ''} 
                        title={testimonials[currentIndex].imageAltText || ''}
                        fill 
                        className="object-cover" 
                      />
                    </div>
                  )}
                  {testimonials[currentIndex].mediaType === 'video' && testimonials[currentIndex].video && (
                    <div 
                      className="relative group/testimonial1 cursor-pointer md:w-[248px] aspect-[248/140] rounded-xl overflow-hidden" 
                      onClick={() => handleVideoClick(testimonials[currentIndex].video!)}
                    >
                      <div className="absolute inset-0 bg-black/20 group-hover/testimonial1:bg-black/30 transition-colors" />
                      <Image
                        src={testimonials[currentIndex].image || getVideoThumbnail(testimonials[currentIndex].video) || ''}
                        alt={testimonials[currentIndex].imageAltText || ''}
                        title={testimonials[currentIndex].imageAltText || ''}
                        fill
                        className="object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex items-center justify-center w-12 h-12">
                          <Button 
                            variant="secondary" 
                            size="iconLg"   
                            className="absolute bottom-4 right-4"
                          >
                            <span className="material-symbols-outlined font-variation-filled">play_arrow</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-0">
                    {testimonials[currentIndex].author && (
                      <h5>{testimonials[currentIndex].author}</h5>
                    )}
                    {testimonials[currentIndex].company && (
                      <p className="paragraph-lg text-muted-foreground">{testimonials[currentIndex].company}</p>
                    )}
                    {testimonials[currentIndex].hasButton && (
                      <div className="flex ">
                        <Button asChild variant={testimonials[currentIndex].buttonStyle} className="mt-6 flex items-center gap-2 w-full lg:w-auto" size="lg">
                          <Link href={testimonials[currentIndex].buttonLink ?? ""}>
                            {testimonials[currentIndex].buttonText}
                            <span className="material-symbols-outlined">arrow_forward</span>
                          </Link>
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {testimonials && testimonials.length > 1 && (
          <div className="container flex gap-3 mt-14 lg:mt-20">
            <Button 
              variant="outline" 
              size="icon" 
              className="static translate-y-0"
              onClick={() => paginate(-1)}
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              className="static translate-y-0"
              onClick={() => paginate(1)}
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </Button>
          </div>
        )}
      </div>

      {/* Background Carousel */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <AnimatePresence initial={false} custom={direction} mode="wait">
          {testimonials && testimonials.length > 0 && testimonials[currentIndex].mediaType && testimonials[currentIndex].image && (
            <motion.div
              key={`bg-${currentIndex}`}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={swipeTransition}
              className="absolute inset-0 w-full h-full"
            >
              <div className="absolute inset-0 opacity-50">
                <Image
                  src={testimonials[currentIndex].image}
                  alt={testimonials[currentIndex].imageAltText || ''}
                  title={testimonials[currentIndex].imageAltText || ''}
                  fill
                  className="object-cover"
                  priority={currentIndex === 0}
                  sizes="100vw"
                  onLoad={() => {
                    // Ensure smooth transition after image loads
                    if (currentIndex === 0) {
                      setIsInitialLoad(false);
                    }
                  }}
                />
              </div>
              <div 
                className={cn(
                  "absolute inset-0 opacity-70", 
                  darkMode ? 'dark' : 'light',
                  sectionBackgroundColors(darkMode, variant),
                )}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {currentVideo && (
        <VideoIframe videoUrl={currentVideo} isOpen={isVideoOpen} onClose={handleCloseVideo} />
      )}
    </SectionWrapper>
  );
};

export { Testimonial1 };
